import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, User } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import api from '../utils/api';
import { toast } from 'sonner';

export default function VehiclesManagement({ user, onLogout }) {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [assigningDriver, setAssigningDriver] = useState({}); // Track loading state per vehicle
  const [formData, setFormData] = useState({
    registration: '',
    max_payload_kg: '',
    category: 'Van',
    notes: '',
    mot_date: '',
    road_tax_date: '',
    service_date: '',
    active: true,
  });

  // Helper to check if date is due soon
  const getDateStatus = (dateStr) => {
    if (!dateStr) return { text: '-', color: 'text-slate-400' };
    
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    // Format date in UK format (DD/MM/YYYY)
    const formattedDate = date.toLocaleDateString('en-GB');
    
    if (daysUntil < 0) {
      return { text: formattedDate, color: 'text-red-600 font-bold', badge: 'OVERDUE' };
    } else if (daysUntil === 0) {
      return { text: formattedDate, color: 'text-red-600 font-bold', badge: 'DUE TODAY' };
    } else if (daysUntil <= 2) {
      return { text: formattedDate, color: 'text-orange-600 font-semibold', badge: `${daysUntil}d` };
    } else if (daysUntil <= 7) {
      return { text: formattedDate, color: 'text-orange-500 font-medium', badge: `${daysUntil}d` };
    } else if (daysUntil <= 30) {
      return { text: formattedDate, color: 'text-amber-600', badge: `${daysUntil}d` };
    } else {
      return { text: formattedDate, color: 'text-slate-700' };
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, driversRes] = await Promise.all([
        api.get('/vehicles'),
        api.get('/drivers')
      ]);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles');
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - convert empty strings to null for date fields
      const submitData = {
        ...formData,
        mot_date: formData.mot_date || null,
        road_tax_date: formData.road_tax_date || null,
        service_date: formData.service_date || null,
      };
      
      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.id}`, submitData);
        toast.success('Vehicle updated successfully');
      } else {
        await api.post('/vehicles', submitData);
        toast.success('Vehicle created successfully');
      }
      setDialogOpen(false);
      setEditingVehicle(null);
      setFormData({ registration: '', max_payload_kg: '', category: 'Van', notes: '', mot_date: '', road_tax_date: '', service_date: '', active: true });
      fetchVehicles();
    } catch (error) {
      console.error('Vehicle operation error:', error);
      
      // Handle Pydantic validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation error array
          const errorMessages = detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Operation failed');
        }
      } else {
        toast.error('Operation failed. Please check your input.');
      }
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      registration: vehicle.registration,
      max_payload_kg: vehicle.max_payload_kg,
      category: vehicle.category || 'Van',
      notes: vehicle.notes || '',
      mot_date: vehicle.mot_date || '',
      road_tax_date: vehicle.road_tax_date || '',
      service_date: vehicle.service_date || '',
      active: vehicle.active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await api.delete(`/vehicles/${id}`);
        toast.success('Vehicle deleted');
        fetchVehicles();
      } catch (error) {
        toast.error('Failed to delete vehicle');
      }
    }
  };

  const handleDriverAssignment = async (vehicleId, driverEmail) => {
    setAssigningDriver(prev => ({ ...prev, [vehicleId]: true }));
    
    try {
      await api.put(`/vehicles/${vehicleId}/assign-driver`, {
        driver_email: driverEmail === 'unassigned' ? null : driverEmail
      });
      
      toast.success(driverEmail === 'unassigned' ? 'Driver unassigned' : 'Driver assigned successfully');
      fetchVehicles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to assign driver';
      toast.error(message);
    } finally {
      setAssigningDriver(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  const resetForm = () => {
    setEditingVehicle(null);
    setFormData({ registration: '', max_payload_kg: '', category: 'Van', notes: '', mot_date: '', road_tax_date: '', service_date: '', active: true });
  };

  // Check if driver is already assigned to another vehicle
  const isDriverAssigned = (driverEmail, currentVehicleId) => {
    return vehicles.some(v => 
      v.assigned_driver_email === driverEmail && v.id !== currentVehicleId
    );
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Vehicles</h1>
            <p className="text-slate-600 mt-2">Manage your fleet vehicles and driver assignments</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-vehicle-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
                <DialogDescription>Enter vehicle details below</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="registration">Registration</Label>
                  <Input
                    id="registration"
                    value={formData.registration}
                    onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                    placeholder="CP24 DDL"
                    required
                    data-testid="vehicle-registration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_payload_kg">Max Payload (kg)</Label>
                  <Input
                    id="max_payload_kg"
                    type="number"
                    value={formData.max_payload_kg}
                    onChange={(e) => setFormData({ ...formData, max_payload_kg: e.target.value })}
                    placeholder="3500"
                    required
                    data-testid="vehicle-payload-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger data-testid="vehicle-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Van">Van</SelectItem>
                      <SelectItem value="Truck">Truck</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    data-testid="vehicle-notes-input"
                  />
                </div>
                
                {/* Maintenance Dates */}
                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700">Maintenance Dates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mot_date" className="text-xs">MOT Date</Label>
                      <Input
                        id="mot_date"
                        type="date"
                        value={formData.mot_date}
                        onChange={(e) => setFormData({ ...formData, mot_date: e.target.value })}
                        data-testid="vehicle-mot-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="road_tax_date" className="text-xs">Road Tax Date</Label>
                      <Input
                        id="road_tax_date"
                        type="date"
                        value={formData.road_tax_date}
                        onChange={(e) => setFormData({ ...formData, road_tax_date: e.target.value })}
                        data-testid="vehicle-roadtax-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_date" className="text-xs">Service Date</Label>
                      <Input
                        id="service_date"
                        type="date"
                        value={formData.service_date}
                        onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                        data-testid="vehicle-service-input"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    data-testid="vehicle-active-checkbox"
                  />
                  <Label htmlFor="active" className="cursor-pointer">Active</Label>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" data-testid="vehicle-submit-button">
                    {editingVehicle ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Vehicles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="vehicles-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Registration</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Max Payload (kg)</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        Driver
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Active</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Notes</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">MOT</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Road Tax</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Service</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-8 text-slate-500">No vehicles found</td>
                    </tr>
                  ) : (
                    vehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4 font-medium">{vehicle.registration}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            vehicle.category === 'Truck' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {vehicle.category || 'Van'}
                          </span>
                        </td>
                        <td className="py-3 px-4">{vehicle.max_payload_kg}</td>
                        <td className="py-3 px-4">
                          <Select
                            value={vehicle.assigned_driver_email || 'unassigned'}
                            onValueChange={(value) => handleDriverAssignment(vehicle.id, value)}
                            disabled={assigningDriver[vehicle.id]}
                          >
                            <SelectTrigger 
                              className="w-[180px]" 
                              data-testid={`driver-select-${vehicle.id}`}
                            >
                              <SelectValue placeholder="Select driver">
                                {assigningDriver[vehicle.id] ? (
                                  <span className="text-slate-400">Updating...</span>
                                ) : vehicle.assigned_driver_name ? (
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {vehicle.assigned_driver_name}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">Unassigned</span>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">
                                <span className="text-slate-400">Unassigned</span>
                              </SelectItem>
                              {drivers.map((driver) => {
                                const alreadyAssigned = isDriverAssigned(driver.email, vehicle.id);
                                return (
                                  <SelectItem 
                                    key={driver.email} 
                                    value={driver.email}
                                    disabled={alreadyAssigned}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{driver.full_name}</span>
                                      {alreadyAssigned && (
                                        <span className="text-xs text-slate-400">(assigned)</span>
                                      )}
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            vehicle.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {vehicle.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{vehicle.notes || '-'}</td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-1">
                            <span className={getDateStatus(vehicle.mot_date).color}>
                              {getDateStatus(vehicle.mot_date).text}
                            </span>
                            {getDateStatus(vehicle.mot_date).badge && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">
                                {getDateStatus(vehicle.mot_date).badge}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-1">
                            <span className={getDateStatus(vehicle.road_tax_date).color}>
                              {getDateStatus(vehicle.road_tax_date).text}
                            </span>
                            {getDateStatus(vehicle.road_tax_date).badge && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">
                                {getDateStatus(vehicle.road_tax_date).badge}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-1">
                            <span className={getDateStatus(vehicle.service_date).color}>
                              {getDateStatus(vehicle.service_date).text}
                            </span>
                            {getDateStatus(vehicle.service_date).badge && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">
                                {getDateStatus(vehicle.service_date).badge}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEdit(vehicle);
                              }} 
                              data-testid={`edit-vehicle-${vehicle.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDelete(vehicle.id);
                              }} 
                              data-testid={`delete-vehicle-${vehicle.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
